import type {ComponentType, LazyExoticComponent} from 'react';
import React from 'react';

import Big from 'big.js';
import dot from 'dot-object';
import type {CountryCode} from 'libphonenumber-js';
import {parsePhoneNumberFromString} from 'libphonenumber-js';
import {divide, multiply, round} from 'mathjs';
import {parseDomain, ParseResultType} from 'parse-domain';

import type {ChargeFieldsFragment} from '../api/generated';

import {omitDeep} from './cleanTypenameLink';

export function enumKeys<E extends Record<string | number | symbol, unknown>>(
  e: E
): Array<keyof E> {
  return Object.keys(e) as Array<keyof E>;
}

export function enumValues<E extends Record<string | number | symbol, unknown>>(
  e: E
): Array<E[keyof E]> {
  return Object.values(e) as Array<E[keyof E]>;
}

/**
 * Calculates percentage rate
 * @param num
 * @param denom
 */
export const getRate = (num = 0, denom = 1): number => {
  if (denom === 0) return 0;
  const ratio = num / denom;
  return (ratio > 1 ? 1 : ratio) * 100;
};

/**
 * Calculates increase in percentages
 * @param first
 * @param second
 */
export const getIncrease = (first = 0, second = 1): number => {
  if (first === 0 && second === 0) return 0;
  if (second === 0) return 100;
  const ratio = (first - second) / second;
  if (ratio > 100) return 100;
  return ratio * 100;
};

export interface ILazyComponent<
  T extends ComponentType<Record<string, unknown>>
> extends LazyExoticComponent<T> {
  preload: () => Promise<unknown>;
}

/**
 * Lazy load component and add preload method
 * Call Component.preload() to fetch async chunks
 * @param factory
 */
export const lazyWithPreload = <T extends ComponentType<Record<string, unknown>>>(
  factory: () => Promise<{default: T}>
): ILazyComponent<T> => {
  const Component = React.lazy(factory) as ILazyComponent<T>;
  Component.preload = factory;
  return Component;
};

/**
 * Get random int in rage
 * @param min
 * @param max
 */
export const getRandomInt = (min: number, max: number): number => {
  return Math.round(Math.random() * (max - min) + min);
};

/**
 *intersperse: Return an array with the separator interspersed between
 * each element of the input array.
 *
 * > intersperse([1,2,3], 0)
 * [1,0,2,0,3]
 */
export function intersperse<T>(arr: T[], sep: T): T[] {
  if (arr.length === 0) return [];
  const first = arr[0];
  if (first === undefined) return [];
  return arr.slice(1).reduce<T[]>((xs, x) => [...xs, sep, x], [first]);
}

/**
 * Checks if variable is undefined or null
 * @param variable
 */
export const isUndefined = (variable: unknown): boolean => {
  return typeof variable === 'undefined' || variable === null;
};

/**
 * Capitalize firs letter
 * @param s - string
 */
export const capitalize = (s: string): string => {
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const downloadTextAsFile = ({
  fileName,
  text,
  type
}: {
  fileName: string;
  text: string;
  type: string;
}): void => {
  const element = document.createElement('a');
  element.setAttribute('href', `data:${type};charset=utf-8,${encodeURIComponent(text)}`);
  element.setAttribute('download', fileName);
  element.style.display = 'none';
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
};

export const downloadFile = (url: string, name?: string): void => {
  const element = document.createElement('a');
  element.setAttribute('href', url);
  element.setAttribute('download', name ?? 'true');
  element.style.display = 'none';
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
};

/**
 * Returns zero if number is negative
 * @param n
 */
export const positiveOrZero = (n: number): number => {
  return n > 0 ? n : 0;
};

/**
 * Makes sure that the object is of type object and is not null.
 */
export const isObject = (object: unknown): object is Record<string, unknown> =>
  object != null && typeof object === 'object';

export const pick = (
  obj: Record<string, unknown> | undefined,
  keys: string[]
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  if (!obj) return result;
  keys.forEach((key) => {
    if (obj[key] === undefined) return;
    result[key] = obj[key];
  });
  return result;
};

export const omit = (
  obj: Record<string, unknown> | undefined,
  keys: string[]
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  if (!obj) return result;
  Object.keys(obj).forEach((key) => {
    if (keys.includes(key)) return;
    result[key] = obj[key];
  });
  return result;
};

export const delay = (ms: number): Promise<void> =>
  new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, ms);
  });

/**
 * Check if object is empty
 * @param o - object to test
 */
export function isEmpty(o?: object): boolean {
  if (!o) return true;
  return Object.keys(o).length === 0;
}

export function nullify<T>(o: T): T {
  // Handle primitives and null
  if (o === '' || o === undefined) return null as T;
  if (o === null || typeof o !== 'object') return o;

  // Handle arrays specially to preserve them as arrays
  if (Array.isArray(o)) {
    return o.map((item) => nullify(item)) as T;
  }

  // Handle objects
  return Object.keys(o).reduce<Record<string, unknown>>((result, key) => {
    const value = (o as Record<string, unknown>)[key];
    result[key] = nullify(value);
    return result;
  }, {}) as T;
}

export const toCents = (amount: number | null = null): number | null => {
  if (amount === null) return null;
  return round(multiply(amount, 100));
};

export const fromCents = (amount: number | null = null): number | null => {
  if (amount === null) return null;
  return round(divide(amount, 100));
};

export const amountToInt = (amount: number): number => {
  return Big(amount).times(100).toNumber();
};

export const amountFromInt = (amount: number): number => {
  return Big(amount).div(100).toNumber();
};

export const unique = <T>(array: T[]): T[] => {
  return Array.from(new Set(array));
};
export const randomHex = (size: number): string => {
  return Array.from({length: size})
    .map(() => Math.floor(Math.random() * 16).toString(16))
    .join('');
};
export const arrayIncludesSome = <T>(array: T[], values: T[]): boolean => {
  return values.some((value) => array.includes(value));
};

export const parseJSON = (string?: string | null): unknown => {
  if (!string) return undefined;
  try {
    return JSON.parse(string);
  } catch {
    return undefined;
  }
};

export const isDefined = (
  value: unknown,
  options: {allowZero?: boolean; allowEmptyString?: boolean} = {
    allowZero: true,
    allowEmptyString: true
  }
): boolean => {
  if (value === '') return options.allowEmptyString ?? true;
  if (value === 0) return options.allowZero ?? true;
  return Boolean(value);
};

export const isQrPayment = (charge: ChargeFieldsFragment): boolean => {
  return !!charge.paymentMethod && (charge.orderId?.startsWith('QR') ?? false);
};

/**
 * Get changed keys from two objects in dot notation
 * @param obj1
 * @param obj2
 */
export const getChangedKeys = (
  obj1: Record<string, unknown> = {},
  obj2: Record<string, unknown> = {}
): string[] => {
  const dotObj1 = dot.dot(obj1) as Record<string, unknown>;
  const dotObj2 = dot.dot(obj2) as Record<string, unknown>;
  const keys = unique(Object.keys(dotObj1).concat(Object.keys(dotObj2)));
  return keys.filter((key) => JSON.stringify(dotObj1[key]) !== JSON.stringify(dotObj2[key]));
};

export const dateFormat = {
  dateTime: 'DD/MM/YY HH:mm',
  localizedShort: 'lll'
};

export const truncate = (
  str: string | null | undefined,
  length: number,
  position: 'end' | 'start' = 'end'
): string => {
  if (typeof str !== 'string') return '';
  if (!str) return '';
  if (str.length <= length) return str;
  if (position === 'start') return `…${str.slice(-length)}`;
  return `${str.substring(0, length)}…`;
};

export const normalizePhone = (
  phone?: string | null,
  countryCode?: string | null
): string | null => {
  if (!phone) return null;
  return parsePhoneNumberFromString(phone, countryCode as CountryCode)?.format('E.164') ?? null;
};

export const openNewTab = (url: string | undefined): void => {
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

export const prettifyEvent = (event: unknown): string =>
  JSON.stringify(
    omitDeep(
      {
        ...(event as Record<string, unknown>),
        changed: undefined
      },
      '__typename'
    ),
    null,
    2
  );

export const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

export const sortObjectKeysByValue = (obj: Record<string, string>): Record<string, string> => {
  const sortedKeys = Object.keys(obj).sort((a, b) => {
    const aVal = obj[a];
    const bVal = obj[b];
    if (aVal === undefined || bVal === undefined) return 0;
    return aVal.localeCompare(bVal);
  });

  return sortedKeys.reduce((acc: Record<string, string>, key) => {
    const value = obj[key];
    if (value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

export const checkInternalEmail = (email?: string | null): boolean => {
  if (!email) return false;
  return email.includes('@monei.com') || email.includes('@microapps.com');
};

export const getHostname = (url?: string | null): string => {
  if (!url) return '';
  return /\/\/([^/]+)/.exec(url)?.[1] ?? '';
};

export const getTopDomain = (hostname: string): string => {
  const result = parseDomain(hostname);
  if (result.type !== ParseResultType.Listed) return '';
  return [result.domain, ...result.topLevelDomains].join('.');
};
